import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["flexWrap", "leftColumnContent", "leftColumnHeader", "leftColumnFooter", "leftColumnMinWidth", "leftColumnScrollDirection", "leftScrollElementRef", "maxHeight", "rightColumnContent", "rightColumnFooter", "rightColumnHeader", "rightColumnScrollDirection", "rightColumnWidth", "rightScrollElementRef", "scrollColumn"];
import { useContext } from 'react';
import styled from 'foundations-theming/utils';
import { ScrollingColumn, ScrollingColumnInner } from '../ScrollingColumn/ScrollingColumn';
import { usePanelContext } from '../../context';
import { DialogContext } from '../Dialog/DialogContext';
import { ScrollingColumnsProvider } from './ScrollingColumnsContext';
import { CSS_SPECIFICITY } from '../../constants/CSSSpecificity';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const StyledContainer = styled.div.withConfig({
  displayName: "ScrollingColumns__StyledContainer",
  componentId: "sc-1sv5mqh-0"
})(({
  $inPopupModal,
  $inPanel,
  $scrollColumn,
  $hasCustomHeight,
  $maxHeight,
  theme
}) => {
  return Object.assign({
    alignSelf: 'stretch',
    display: 'flex',
    height: '100%',
    marginLeft: `-${theme.space['500']}`,
    marginTop: `-${theme.space['500']}`,
    width: `calc(100% + ${theme.space['500']})`
  }, $maxHeight && {
    [CSS_SPECIFICITY.OVERRIDE]: {
      maxHeight: $maxHeight
    }
  }, {
    '& [data-scrolling-column="right"]': {
      borderLeftWidth: theme.borderWidth['100'],
      borderLeftStyle: 'solid',
      borderLeftColor: theme.color['border-core-subtle']
    }
  }, $inPopupModal && {
    '@media only screen and (min-width: 830px)': Object.assign({
      height: 'auto',
      maxHeight: '400px',
      minHeight: '0'
    }, $scrollColumn !== 'both' && {
      [CSS_SPECIFICITY.OVERRIDE]: {
        maxHeight: `calc(100% + ${theme.space['500']})`
      },
      // Target the inner wrapper to set flex-basis: auto
      [`& ${ScrollingColumnInner}`]: {
        flexBasis: 'auto'
      }
    }, $hasCustomHeight && {
      maxHeight: '100%',
      // Target the inner wrapper to set flex-basis: auto
      [`& ${ScrollingColumnInner}`]: {
        flexBasis: 'auto'
      }
    }, {
      // Column height auto in popup modals
      '& [data-scrolling-column]': {
        height: 'auto'
      }
    }, $scrollColumn === 'left' && {
      [`& [data-scrolling-column="left"] ${ScrollingColumnInner}`]: {
        height: '100%',
        position: 'absolute',
        width: `calc(100% - ${theme.space['500']} - ${theme.space['500']})` // Double padding for left
      }
    }, $scrollColumn === 'right' && {
      [`& [data-scrolling-column="right"] ${ScrollingColumnInner}`]: {
        height: '100%',
        position: 'absolute',
        width: `calc(100% - ${theme.space['500']})` // Single padding for right
      }
    }),
    '@media only screen and (max-width: 544px)': Object.assign({
      flexWrap: 'wrap',
      '& [data-scrolling-column="right"]': {
        borderWidth: theme.borderWidth['0']
      },
      // Mobile left column padding override
      '& [data-scrolling-column="left"]': {
        paddingRight: '0'
      }
    }, $hasCustomHeight && {
      [`& ${ScrollingColumnInner}`]: {
        maxHeight: '100% !important'
      }
    })
  }, $inPanel && {
    '& [data-scrolling-column]': {
      height: '100%',
      minHeight: '0',
      width: '100%'
    },
    [`& ${ScrollingColumnInner}`]: {
      flexBasis: 'auto'
    }
  });
});
export const ScrollingColumns = _ref => {
  let {
      flexWrap = 'wrap',
      leftColumnContent,
      leftColumnHeader,
      leftColumnFooter,
      leftColumnMinWidth = '250px',
      leftColumnScrollDirection = 'vertical',
      leftScrollElementRef,
      maxHeight,
      rightColumnContent,
      rightColumnFooter,
      rightColumnHeader,
      rightColumnScrollDirection = 'vertical',
      rightColumnWidth = '40%',
      rightScrollElementRef,
      scrollColumn = 'both'
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  const {
    inModal,
    inPanel
  } = usePanelContext();
  const dialogContext = useContext(DialogContext);

  // Only apply 400px height constraint for popup modals, not fullscreen
  const inPopupModal = inModal && (dialogContext === null || dialogContext === void 0 ? void 0 : dialogContext.use) && dialogContext.use !== 'fullscreen' && dialogContext.use !== 'lightbox' && dialogContext.use !== 'sidebar';
  const hasCustomHeight = maxHeight !== undefined;
  return /*#__PURE__*/_jsx(ScrollingColumnsProvider, {
    value: {
      multiColumn: true
    },
    children: /*#__PURE__*/_jsxs(StyledContainer, Object.assign({}, rest, {
      $inPopupModal: inPopupModal,
      $inPanel: inPanel,
      $scrollColumn: scrollColumn,
      $hasCustomHeight: hasCustomHeight,
      $maxHeight: maxHeight,
      children: [/*#__PURE__*/_jsx(ScrollingColumn, {
        "data-scrolling-column": "left",
        paddingInlineStart: "500",
        paddingInlineEnd: "500",
        paddingBlockEnd: flexWrap === 'wrap' ? '400' : '0',
        marginBlockStart: "500",
        minInlineSize: '0',
        blockSize: inPanel ? '100%' : 'auto',
        position: "relative",
        flexBasis: leftColumnMinWidth,
        flexGrow: 9999,
        flexShrink: 1,
        footerContent: leftColumnFooter,
        headerContent: leftColumnHeader,
        maxHeight: maxHeight,
        scrollDirection: leftColumnScrollDirection,
        scrollElementRef: leftScrollElementRef,
        children: leftColumnContent
      }), /*#__PURE__*/_jsx(ScrollingColumn, {
        "data-scrolling-column": "right",
        paddingInlineStart: "500",
        marginBlockStart: "500",
        minInlineSize: '0',
        blockSize: inPanel ? '100%' : 'auto',
        position: "relative",
        flexBasis: rightColumnWidth,
        flexGrow: 1,
        flexShrink: 1,
        footerContent: rightColumnFooter,
        headerContent: rightColumnHeader,
        maxHeight: maxHeight,
        scrollDirection: rightColumnScrollDirection,
        scrollElementRef: rightScrollElementRef,
        children: rightColumnContent
      })]
    }))
  });
};